import { RequirementType } from "@wingspanhq/wingspan-api-sdk";

import { queriesInsuranceMonitors } from "../../../v3/query/queriesInsuranceMonitors";
import { queriesSharedFileRequests } from "../../../v3/query/queriesSharedFileRequests";
import { queriesSignatureRequests } from "../../../v3/query/queriesSignatureRequests";
import { getRequirementDefinitionName } from "../../RequirementDefinitions/utils";
import { REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS } from "../modules/EmployeeTaxForms/constants";
import { INSURANCE_MONITOR_EXPAND } from "../modules/InsuranceCoverage/constants";
import { selectorPolicyTypesFromMonitor } from "../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromMonitor";
import { isSupportedRequirementType, SupportedRequirementType } from "../types";

export const usePayeeRequirementNames = (props: {
  requirementType: RequirementType;
  requirementDataSourceId: string;
  /**
   * The requirement instance's own `name` — the display label for data sources
   * that carry no title on v3 (BackgroundCheck, InsuranceMonitor).
   */
  requirementName?: string;
}) => {
  const { requirementType, requirementDataSourceId, requirementName } = props;

  const querySignatureRequest =
    queriesSignatureRequests.getSignatureRequest.useQuery(
      { requestId: requirementDataSourceId },
      { enabled: requirementType === RequirementType.SIGNATURE }
    );

  const querySharedFileRequest =
    queriesSharedFileRequests.getSharedFileRequest.useQuery(
      { sharedFileRequestId: requirementDataSourceId },
      { enabled: requirementType === RequirementType.DOCUMENT_UPLOAD }
    );

  const queryInsuranceMonitor =
    queriesInsuranceMonitors.getInsuranceMonitor.useQuery(
      {
        monitorId: requirementDataSourceId,
        expand: INSURANCE_MONITOR_EXPAND
      },
      { enabled: requirementType === RequirementType.INSURANCE_COVERAGE }
    );

  const nameByDefinition = getRequirementDefinitionName({
    type: requirementType,
    contextType: querySharedFileRequest.data?.contextType,
    policyType: selectorPolicyTypesFromMonitor(queryInsuranceMonitor.data)
  });

  const namesMapByDataSource: Record<
    SupportedRequirementType,
    string | undefined
  > = {
    [RequirementType.SIGNATURE]: querySignatureRequest.data?.title,
    [RequirementType.DOCUMENT_UPLOAD]: querySharedFileRequest.data?.title,
    // v3 BackgroundCheck carries no title — the requirement's own name is the label
    [RequirementType.BACKGROUND_CHECK]: requirementName,
    [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: nameByDefinition,
    // v3 InsuranceMonitor carries no title — requirement name, else the
    // coverage-type label folded into the definition name above
    [RequirementType.INSURANCE_COVERAGE]: requirementName ?? nameByDefinition,
    [RequirementType.REGISTRATION]: nameByDefinition,
    [RequirementType.TAX_VERIFICATION]: nameByDefinition,
    [RequirementType.PAYOUT_METHOD]: nameByDefinition,
    [REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS]: nameByDefinition
  };

  const loaders: Record<SupportedRequirementType, boolean> = {
    [RequirementType.SIGNATURE]: querySignatureRequest.isLoading,
    [RequirementType.DOCUMENT_UPLOAD]: querySharedFileRequest.isLoading,
    [RequirementType.BACKGROUND_CHECK]: false,
    [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: false,
    [RequirementType.INSURANCE_COVERAGE]: false,
    [RequirementType.REGISTRATION]: false,
    [RequirementType.TAX_VERIFICATION]: false,
    [RequirementType.PAYOUT_METHOD]: false,
    [REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS]: false
  };

  const isSupported = isSupportedRequirementType(requirementType);

  return {
    isLoading: isSupported ? loaders[requirementType] : false,
    name: isSupported
      ? namesMapByDataSource[requirementType] || nameByDefinition
      : nameByDefinition,
    definitionName: nameByDefinition
  };
};
